// ./src/hooks/useCategories.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Category,
  CategoryService,
  CategoryFilter,
  CategoryInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseCategoriesOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<CategoryFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;

  userId?: string | null;
}

export interface UseCategoriesReturn {
  // Data state
  category: Category | null;
  categories: Category[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<CategoryFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingCategoryId: string | null;
  selectedCategory: Category | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<CategoryFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Category[]>;
  create: (params: CategoryInput) => Promise<Category | null>;
  update: (id: string, params: CategoryInput) => Promise<Category | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  ensureCategories: (categories: Category[] | null | undefined) => void;

  // Search and filter operations
  setFilters: (filters: Partial<CategoryFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedCategory: (category: Category | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  categoriesByName: Record<string, Category>;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [{ key: "name", direction: "asc" }];

export const useCategories = (
  options: UseCategoriesOptions = {}
): UseCategoriesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
    userId = null,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [category, setCategory] = useState<Category | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadedForUserId, setLoadedForUserId] = useState<string | null>(null);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<CategoryFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingCategoryId, setRemovingCategoryId] = useState<string | null>(
    null
  );
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => categories.length === 0 && !loading,
    [categories.length, loading]
  );

  const categoriesByName = useMemo(() => {
    return categories.reduce((acc, category) => {
      if (category.id && category.name) {
        acc[category.name] = category;
      }
      return acc;
    }, {} as Record<string, Category>);
  }, [categories]);

  // Helper function to update category in list if it exists, or add it if not
  const updateOrAddCategoryInList = useCallback((updatedCategory: Category) => {
    setCategories((prev) => {
      const existingIndex = prev.findIndex((c) => c.id === updatedCategory.id);
      if (existingIndex >= 0) {
        const newCategories = [...prev];
        newCategories[existingIndex] = updatedCategory;
        return newCategories;
      } else {
        return [updatedCategory, ...prev];
      }
    });
  }, []);

  // Helper function to remove category from list if it exists
  const removeCategoryFromList = useCallback((categoryId: string) => {
    setCategories((prev) => prev.filter((c) => c.id !== categoryId));
  }, []);

  // Helper function to remove multiple categories from list
  const removeCategoriesFromList = useCallback((categoryIds: string[]) => {
    const idsSet = new Set(categoryIds);
    setCategories((prev) => prev.filter((c) => !idsSet.has(c.id!)));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    console.log("Resetting all the categories");
    setCategory(null);
    setCategories([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingCategoryId(null);
    setSelectedCategory(null);
  }, []);

  // Get single category (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await CategoryService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const categoryData = result.data[0] as Category;
          setCategory(categoryData);
          updateOrAddCategoryInList(categoryData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load category";
          setLastError(errorMessage);
          setCategory(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load category";
        setLastError(errorMessage);
        setCategory(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddCategoryInList]
  );

  // List categories (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<CategoryFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setCategories([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await CategoryService.list(
          filter as CategoryFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newCategories = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setCategories((prev) => {
              const existingIds = new Set(prev.map((c) => c.id));
              const uniqueNewCategories = newCategories.filter(
                (c) => !existingIds.has(c.id)
              );
              return [...prev, ...uniqueNewCategories];
            });
          } else {
            setCategories(newCategories);
          }

          setHasMore(newCategories.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load categories";
          setLastError(errorMessage);
          if (!isAppending) setCategories([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load categories";
        setLastError(errorMessage);
        if (!isAppending) setCategories([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many categories by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Category[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await CategoryService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedCategories = Array.isArray(result.data)
            ? result.data
            : [];
          loadedCategories.forEach((category) => {
            updateOrAddCategoryInList(category);
          });
          setLastError(null);
          return loadedCategories;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load categories";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load categories";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddCategoryInList]
  );

  // Create category (matching API method name)
  const create = useCallback(
    async (params: CategoryInput): Promise<Category | null> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await CategoryService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newCategory = result.data[0] as Category;
          setCategory(newCategory);
          setCategories((prev) => [newCategory, ...prev]);
          setLastError(null);
          return newCategory;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to create category";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to create category";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update category (matching API method name)
  const update = useCallback(
    async (id: string, params: CategoryInput): Promise<Category | null> => {
      if (!id) {
        setLastError("Category ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await CategoryService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedCategory = result.data[0] as Category;
          setCategory(updatedCategory);
          updateOrAddCategoryInList(updatedCategory);
          setLastError(null);
          return updatedCategory;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update category";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to update category";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddCategoryInList]
  );

  // Remove category (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Category ID is required for removal");
        return false;
      }

      setRemovingCategoryId(id);
      setLastError(null);

      try {
        const result = await CategoryService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setCategory(null);
          removeCategoryFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove category";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to remove category";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingCategoryId(null);
      }
    },
    [removeCategoryFromList]
  );

  // Remove many categories (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("Category IDs are required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await CategoryService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          removeCategoriesFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove categories";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove categories";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeCategoriesFromList]
  );

  // Ensure categories exist in the list (add if not already present)
  const ensureCategories = useCallback(
    (categoriesToEnsure: Category[] | null | undefined) => {
      if (!categoriesToEnsure || categoriesToEnsure.length === 0) {
        return;
      }

      setCategories((prev) => {
        // Create a set of existing category IDs for fast lookup
        const existingIds = new Set(prev.map((c) => c.id));

        // Filter out categories that already exist
        const newCategories = categoriesToEnsure.filter(
          (category) => category.id && !existingIds.has(category.id)
        );

        // If no new categories to add, return previous state
        if (newCategories.length === 0) {
          return prev;
        }

        // Add new categories to the list
        return [...prev, ...newCategories];
      });
    },
    []
  );

  // Load more categories
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh categories
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  useEffect(() => {
    if (autoLoad && isAuthenticated && loadedForUserId != userId) {
      setLoadedForUserId(userId);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [
    autoLoad,
    isAuthenticated,
    userId,
    loadedForUserId,
    list,
    pageSize,
    initialSort,
  ]);

  return {
    category,
    categories,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isCreating,
    isUpdating,
    removingCategoryId,
    selectedCategory,
    get,
    list,
    getMany,
    create,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    ensureCategories,
    setFilters,
    setSortBy,
    setSelectedCategory,
    reset,
    categoriesByName,
    lastError,
    clearError,
  };
};
